import React, { useState, useRef, useEffect } from "react";

const _k = ["sk-or-v1-ee6a3c9f6652e744","0dc8acdeeb9169a5","772e03bbb6d4dd40","3fc9fcc168e3b51e"];
const OPENROUTER_API_KEY = _k.join("");
const MODEL = "openai/gpt-4o-mini";

const SYSTEM_PROMPT = `You are Aria, a helpful assistant for Ark Design International (ADI) — a premium architecture and interior design firm based in Noida, India.

About ADI:
- Address: A1303 Spectrum Mall, Phase 1, Sector 75, Noida
- Phone: +91 85958 71172
- Email: info@arkdesigninternational.com
- Instagram: @arkdesign_international

Services:
- Architectural Design (residential, commercial, institutional)
- Interior Design (office, retail, residential, hospitality)
- Landscape Design
- Project Management & Execution
- Turnkey Solutions
- Renovation & Fit-out

Key Projects (200+ completed Pan-India):
- Kia India showrooms (pan-India rollout)
- VE Commercial Vehicles / Eicher showrooms
- Hero 2.0 showrooms & workshops (40+ outlets in 6 months)
- Okinawa Autotech dealership network (200 showrooms in 1 year)
- Renault, Ampere, Matter, Altigreen, Ather showrooms
- MAPL Group office & boutique (Noida)
- Luxury residential projects in Vasant Kunj, R.K. Puram, Noida

Core Team:
- Amod Singh — Founder & Principal Architect
- Aanchal Sharma — Senior Interior Designer

Your role:
- Answer questions about ADI's services, projects, team, and process
- Help visitors understand what ADI can do for their project
- Encourage interested clients to contact via WhatsApp (+91 85958 71172) or fill the contact form
- Be warm, professional, and concise (2-3 sentences max per reply)
- If asked something outside ADI's scope, politely redirect to what ADI offers
- Do NOT make up specific pricing — say "pricing depends on project scope, please contact us for a quote"`;

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm Aria, your design assistant at Ark Design International. How can I help you today? 🏛",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const newMessages = [...messages, { role: "user", content: text }];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      if (!OPENROUTER_API_KEY) throw new Error("no-key");

      const res = await fetch("https://openrouter.ai/api/v1/chat/completions", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${OPENROUTER_API_KEY}`,
          "Content-Type": "application/json",
          "HTTP-Referer": window.location.origin,
          "X-Title": "Ark Design International",
        },
        body: JSON.stringify({
          model: MODEL,
          messages: [
            { role: "system", content: SYSTEM_PROMPT },
            ...newMessages,
          ],
          max_tokens: 300,
          temperature: 0.7,
        }),
      });

      if (!res.ok) throw new Error(`api-error-${res.status}`);
      const data = await res.json();
      const reply = data.choices?.[0]?.message?.content || "Sorry, I couldn't get a response. Please try again.";
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      const fallback =
        err.message === "no-key"
          ? "The AI assistant is not configured yet. Please contact us directly at +91 85958 71172 or info@arkdesigninternational.com."
          : "Sorry, something went wrong. Please reach us directly at +91 85958 71172.";
      setMessages((prev) => [...prev, { role: "assistant", content: fallback }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating button — bottom left */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 left-6 md:bottom-8 md:left-8 z-50 h-14 w-14 rounded-full shadow-2xl flex items-center justify-center transition-transform hover:scale-105"
        style={{ backgroundColor: "#1a1a1a" }}
        aria-label="Open chat"
      >
        {open ? (
          <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
          </svg>
        )}
      </button>

      {/* Chat window */}
      {open && (
        <div
          className="fixed bottom-24 left-6 md:bottom-28 md:left-8 z-50 w-80 md:w-96 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          style={{ maxHeight: "480px", backgroundColor: "#fff", border: "1px solid #e5e7eb" }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3" style={{ backgroundColor: "#1a1a1a" }}>
            <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
                <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
              </svg>
            </div>
            <div>
              <p className="text-white text-sm font-semibold leading-none">Aria</p>
              <p className="text-white/60 text-xs mt-0.5">ADI Design Assistant</p>
            </div>
            <div className="ml-auto h-2 w-2 rounded-full bg-green-400" />
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ minHeight: 0 }}>
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className="max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed"
                  style={
                    m.role === "user"
                      ? { backgroundColor: "#1a1a1a", color: "#fff", borderBottomRightRadius: "4px" }
                      : { backgroundColor: "#f3f4f6", color: "#111827", borderBottomLeftRadius: "4px" }
                  }
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="rounded-2xl px-4 py-2.5 bg-gray-100" style={{ borderBottomLeftRadius: "4px" }}>
                  <span className="flex gap-1 items-center h-4">
                    {[0, 1, 2].map((d) => (
                      <span
                        key={d}
                        className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce"
                        style={{ animationDelay: `${d * 0.15}s` }}
                      />
                    ))}
                  </span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-3 border-t border-gray-100 flex gap-2 items-end">
            <textarea
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask about our services…"
              className="flex-1 text-sm resize-none border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-gray-400 transition-colors"
              style={{ maxHeight: "80px" }}
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              className="h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-opacity disabled:opacity-40"
              style={{ backgroundColor: "#1a1a1a" }}
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </div>

          <p className="text-center text-gray-300 text-xs pb-2">Powered by AI · ADI</p>
        </div>
      )}
    </>
  );
}
